import {
    CoreFetch,
    createRequestFilters,
    FilterMethod,
    ICustomFilter,
    ListSupportProfessionalClaimsOrderEnum,
    ProfessionalApi,
    SupportCreateProfessionalClaimTemplatePayload,
    SupportPatchProfessionalClaimTemplatePayload,
    SupportProfessionalClaimTemplate,
    SupportProfessionalTemplateServiceLine,
    SupportProfessionalTemplateSortType,
    SupportReplaceProfessionalTemplatePayload,
    SupportUpdateProfessionalClaimTemplatePayload,
} from '@enter/core';
import _ from 'lodash';
import { action, runInAction } from 'mobx';
import { IFetchResourceRequest } from '../services/store/store.service';
import { IResourceStore, ResourceStore } from './resource.store';

export class ProfessionalTemplatesStore extends ResourceStore<SupportProfessionalClaimTemplate>
    implements IResourceStore<SupportProfessionalClaimTemplate> {
    public static getDisplayName(professional: SupportProfessionalClaimTemplate): string {
        return professional.name;
    }

    public static getId(professional: SupportProfessionalClaimTemplate): string {
        return professional.id || '';
    }

    public static getLineDisplayName(serviceLine: SupportProfessionalTemplateServiceLine): string {
        const procedure = serviceLine.f24dProcedureCode && `Procedure: ${serviceLine.f24dProcedureCode}`;
        const amount =
            serviceLine.f24fChargeAmount &&
            `${serviceLine.f24fChargeAmount.currency} ${serviceLine.f24fChargeAmount.amount}`;

        return _.compact([procedure, amount]).join(', ');
    }

    public static async getByQuery(query: string): Promise<SupportProfessionalClaimTemplate[]> {
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterName',
                filterMethod: FilterMethod.LK,
                filterType: 'String',
                value: query,
            },
        ];

        const reqFilters = createRequestFilters(filters);
        return CoreFetch<ProfessionalApi>(ProfessionalApi).listSupportProfessionalClaimTemplates({
            ...reqFilters,
        });
    }

    public static async getOne(id: string): Promise<SupportProfessionalClaimTemplate> {
        return CoreFetch<ProfessionalApi>(ProfessionalApi).getSupportProfessionalClaimTemplate({
            id,
        });
    }

    public static async getByClaimId(claimId: string): Promise<SupportProfessionalClaimTemplate | undefined> {
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterClaimId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: claimId,
            },
        ];
        const reqFilters = createRequestFilters(filters);

        const professionalClaims = await CoreFetch<ProfessionalApi>(ProfessionalApi).listSupportProfessionalClaims({
            ...reqFilters,
        });

        const professionalClaim = _.first(professionalClaims);

        if (!professionalClaim?.professionalTemplateId) {
            return;
        }

        return CoreFetch<ProfessionalApi>(ProfessionalApi).getSupportProfessionalClaimTemplate({
            id: professionalClaim.professionalTemplateId,
        });
    }

    public static edit = async (id: string, payload: SupportUpdateProfessionalClaimTemplatePayload): Promise<void> => {
        throw new Error('API no longer support this action');
    };

    public static patch = async (id: string, payload: SupportPatchProfessionalClaimTemplatePayload): Promise<void> => {
        throw new Error('API no longer support this action');
    };

    public static filterUnvalidPayload = <D extends object>(
        payload: D & Pick<SupportPatchProfessionalClaimTemplatePayload, 'f24ServiceLine'>,
    ): D & Pick<SupportPatchProfessionalClaimTemplatePayload, 'f24ServiceLine'> => {
        return {
            ...payload,
            f24ServiceLine: payload.f24ServiceLine
                ? _.map(payload.f24ServiceLine, line => {
                      return {
                          ...line,
                          x12Ambulance: maybeFilterOut(line.x12Ambulance, [
                              'x12Distance',
                              'x12PatientCount',
                              'x12PatientWeight',
                          ]),
                          x12Anesthesia: maybeFilterOut(line.x12Anesthesia, ['x12Minutes', 'x12ObstetricUnits']),
                          x12DurableMedicalEquipment: maybeFilterOut(line.x12DurableMedicalEquipment, [
                              'x12CertificationDuration',
                              'x12LengthOfMedicalNecessity',
                          ]),
                      };
                  })
                : undefined,
        };
    };

    public fetch = async (req?: IFetchResourceRequest): Promise<void> => {
        runInAction(() => {

            this.loading = true;

        });

        const reqOptions = this.handleCreateRequestOptions(
            req,
            SupportProfessionalTemplateSortType,
            ListSupportProfessionalClaimsOrderEnum,
        );
        const raw = await CoreFetch<ProfessionalApi>(ProfessionalApi).listSupportProfessionalClaimTemplatesRaw(
            reqOptions,
        );

        await this.handleFetchResource(raw);

        runInAction(() => {


            this.loading = false;


        });
    };

    public create = async (payload: SupportCreateProfessionalClaimTemplatePayload): Promise<void> => {
        runInAction(() => {

            this.loading = true;

        });
        await CoreFetch<ProfessionalApi>(ProfessionalApi).createSupportProfessionalClaimTemplate({
            supportCreateProfessionalClaimTemplatePayload: payload,
        });
        runInAction(() => {

            this.loading = false;

        });
    };

    public edit = async (id: string, payload: SupportUpdateProfessionalClaimTemplatePayload): Promise<void> => {
        throw new Error('API no longer support this action');
    };

    public patch = async (id: string, payload: SupportPatchProfessionalClaimTemplatePayload): Promise<void> => {
        throw new Error('API no longer support this action');
    };

    public delete = async (id: string): Promise<void> => {
        await CoreFetch<ProfessionalApi>(ProfessionalApi).disableSupportProfessionalClaimTemplate({
            id,
        });
    };

    public replace = async (payload: SupportReplaceProfessionalTemplatePayload): Promise<void> => {
        await CoreFetch<ProfessionalApi>(ProfessionalApi).replaceSupportProfessionalTemplate({
            supportReplaceProfessionalTemplatePayload: payload,
        });
    };
}

export default new ProfessionalTemplatesStore();

function maybeFilterOut<O extends object, K extends keyof O>(obj: O | undefined, keys: K[]): O | undefined {
    if (!obj) {
        return;
    }

    const isEmpty = keys.every(key => _.isEmpty(obj[key]));

    return isEmpty ? undefined : obj;
}
